import {
  AnswerStatusMark,
  IStyles,
  SquareImageAvatar,
  StatusRadioMark,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import {
  ILiveQuizAnswerStatus,
  ILiveQuizParticipant,
} from '../reducer/liveQuiz.slice';

// Mirrors ClickerAssessment's ResponseList visual pattern (same bordered
// scrollable card, SquareImageAvatar + status mark per row) — driven by
// LiveQuiz's own joined-participants + live response data instead of
// clicker-hardware keypad data.
interface IProps {
  showResponse: boolean;
  participants: ILiveQuizParticipant[];
  responses: Record<string, ILiveQuizAnswerStatus>;
}

const styles: IStyles = {
  root: {
    width: { xs: '98%', md: '100%' },
    borderRadius: { xs: pxToRem(11), md: pxTovW(15) },
    backgroundColor: '#FFFFFF',
    boxSizing: 'border-box',
    boxShadow: {
      xs: `0px 0px ${pxToRem(11)} #00000029`,
      md: `0px 0px ${pxTovW(11)} #00000029`,
    },
    padding: {
      xs: `${pxToRem(12)} ${pxToRem(14)}`,
      md: `${pxTovW(0)} ${pxTovW(20)} ${pxTovW(20)} ${pxTovW(20)}`,
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&::-webkit-scrollbar': { display: 'none' },
    maxHeight: { xs: pxToRem(400), md: pxTovW(750), lg: pxTovW(500) },
    overflowY: 'auto',
  },
};

export const LiveQuizResponseList = (props: IProps) => {
  const { showResponse, participants, responses } = props;

  if (!participants.length) return null;

  return (
    <Box sx={styles.root}>
      {participants.map((student) => {
        const response = responses[student.studentId];
        return (
          <Box
            key={student.studentId}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingY: { xs: pxToRem(15), md: pxTovW(21) },
              borderBottom: '1px solid lightGrey',
            }}
          >
            <Box
              sx={{
                maxWidth: { xs: '80%', md: '70%' },
                display: 'flex',
                alignItems: 'center',
                gap: { xs: pxToRem(10), md: pxTovW(25) },
                textAlign: 'left',
              }}
            >
              <SquareImageAvatar
                size="small"
                name={student.name}
                ImageSource={student.profilePicUrl}
                online={student?.isOnline}
              />
              <Typography
                variant="h4"
                sx={{
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  maxHeight: pxToRem(40),
                  maxWidth: '90%',
                }}
              >
                {student.name}
              </Typography>
            </Box>

            <Box>
              {showResponse && response?.isCorrect !== undefined ? (
                <AnswerStatusMark
                  statusmark={response.isCorrect ? 'on' : 'off'}
                />
              ) : (
                <StatusRadioMark status={response ? 'on' : 'off'} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default LiveQuizResponseList;
